import { FC, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PushNotifications } from "@capacitor/push-notifications";
import { StatusBar, Style } from "@capacitor/status-bar";

import { isNativeApp, nativePlatform } from "./native";

/**
 * Maps an incoming deep link to an internal SPA route, or returns null.
 * Handles both forms:
 *  - universal/app links:  https://runningmalta.com/thankyou?ref=…
 *  - custom scheme:        runningmalta://thankyou?ref=…  (host parses as the path)
 */
export const deepLinkToRoute = (url: string): string | null => {
  try {
    const parsed = new URL(url);
    if (parsed.protocol === "runningmalta:") {
      const path = "/" + parsed.hostname + (parsed.pathname || "");
      return path.replace(/\/+$/, "") + parsed.search;
    }
    if (
      parsed.hostname === "runningmalta.com" ||
      parsed.hostname.endsWith(".runningmalta.com")
    ) {
      return parsed.pathname + parsed.search;
    }
  } catch (err) {
    // not a URL we understand
  }
  return null;
};

/**
 * Native-shell glue, rendered (as null) inside <BrowserRouter> so it can navigate:
 *  - deep links (warm + cold start) → SPA routes, closing any in-app browser
 *  - Android hardware back → history back, or minimize on the root screen
 *  - iOS status bar style over the dark header
 */
const AppUrlListener: FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isNativeApp()) return;

    const listeners: Array<Promise<{ remove: () => Promise<void> }>> = [];

    listeners.push(
      CapApp.addListener("appUrlOpen", ({ url }) => {
        const route = deepLinkToRoute(url);
        if (route) {
          Browser.close().catch(() => {}); // no-op on Android; the app link foregrounds us anyway
          navigate(route);
        }
      })
    );

    // Cold start via a deep link.
    CapApp.getLaunchUrl().then((launch) => {
      const route = launch?.url ? deepLinkToRoute(launch.url) : null;
      if (route) navigate(route);
    });

    // Tapping a push notification takes the organizer to the screen it's about.
    listeners.push(
      PushNotifications.addListener("pushNotificationActionPerformed", (action) => {
        navigate(action.notification?.data?.route || "/admin/manage");
      })
    );

    listeners.push(
      CapApp.addListener("backButton", ({ canGoBack }) => {
        if (canGoBack) {
          window.history.back();
        } else {
          CapApp.minimizeApp();
        }
      })
    );

    if (nativePlatform() === "ios") {
      // Light text over the dark brand header.
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
    }

    return () => {
      listeners.forEach((p) => p.then((l) => l.remove()).catch(() => {}));
    };
  }, [navigate]);

  return null;
};

export default AppUrlListener;
